/* eslint-disable */
import { colors } from '../../helper/constant/colors';
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { calculateDistanceKm, escapeHtml } from './jobUtils';
import './JobEmployerMap.css';

// Fix Leaflet default icon path issues
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const BANGKOK_CENTER = [13.7563, 100.5018];
const DEFAULT_ZOOM = 11;
const NEAR_ME_RADIUS_KM = 30;

const buildPinIcon = (item, isNearby) => {
  const rawSrc = item.avatar || item.logo || item.cover || '';
  const imgSrc = String(rawSrc).trim();
  const hasImg = !!imgSrc;
  const nearbyClass = isNearby ? ' nearby' : '';
  return L.divIcon({
    className: 'job-map-pin',
    html: `
      <div class="job-map-pin-inner${nearbyClass}">
        ${hasImg ? `<img class="job-map-pin-avatar" src="${imgSrc}" alt="" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" />` : ''}
        <div class="job-map-pin-placeholder" style="${hasImg ? 'display:none;' : 'display:flex;'}"><i class="mdi mdi-account"></i></div>
        <div class="job-map-pin-tail"></div>
      </div>
    `,
    iconSize: [48, 56],
    iconAnchor: [24, 56],
    popupAnchor: [0, -50],
  });
};

function JobEmployerMap({
  jobs = [],
  items = null,
  mode = 'jobs',
  onClose,
  onToast,
  initialUserCoords = null,
  onSelectCandidate = null,
}) {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const userMarkerRef = useRef(null);
  const radiusCircleRef = useRef(null);
  const hasInitialPositionedRef = useRef(false);
  const userInteractedRef = useRef(false);
  const isMountedRef = useRef(true);

  const [nearHint, setNearHint] = useState('');
  const [locating, setLocating] = useState(false);
  const [nearbyIds, setNearbyIds] = useState([]);

  const displayItems = items || jobs || [];
  const isCandidateMode = mode === 'candidates';

  const itemsWithCoords = useMemo(() => {
    return displayItems.filter((j) => typeof j.lat === 'number' && typeof j.lng === 'number');
  }, [displayItems]);

  const jobsKey = itemsWithCoords.map((j) => `${j.id}:${j.lat}:${j.lng}`).join('|');

  const handleNearMe = useCallback(
    (customCoords = null, forceCenter = false) => {
      const applyCoords = (latitude, longitude) => {
        const map = mapInstanceRef.current;
        if (!map || !map._mapPane) return;
        hasInitialPositionedRef.current = true;

        if (userMarkerRef.current) {
          try {
            userMarkerRef.current.remove();
          } catch (err) { console.debug('[JobEmployerMap] Map operation skipped:', err?.message); }
        }
        if (radiusCircleRef.current) {
          try {
            radiusCircleRef.current.remove();
          } catch (err) { console.debug('[JobEmployerMap] Map operation skipped:', err?.message); }
        }

        userMarkerRef.current = L.circleMarker([latitude, longitude], {
          radius: 9,
          color: colors.blueLight,
          fillColor: colors.blue600,
          fillOpacity: 0.95,
          weight: 3,
        })
          .bindPopup('📍 คุณอยู่ที่นี่')
          .addTo(map);

        radiusCircleRef.current = L.circle([latitude, longitude], {
          radius: NEAR_ME_RADIUS_KM * 1000,
          color: colors.blueLight,
          fillColor: colors.blueLight,
          fillOpacity: 0.1,
          weight: 1.5,
        }).addTo(map);

        const nearby = itemsWithCoords.filter(
          (j) => calculateDistanceKm(latitude, longitude, j.lat, j.lng) <= NEAR_ME_RADIUS_KM,
        );
        if (isMountedRef.current) {
          setNearbyIds(nearby.map((j) => j.id));
          setNearHint(
            nearby.length
              ? isCandidateMode
                ? `📍 แสดงตำแหน่งใกล้คุณ — พบ ${nearby.length} ผู้หางานในรัศมี ${NEAR_ME_RADIUS_KM} กม.`
                : `📍 แสดงตำแหน่งใกล้คุณ — พบ ${nearby.length} บริษัทในรัศมี ${NEAR_ME_RADIUS_KM} กม.`
              : isCandidateMode
                ? `📍 แสดงตำแหน่งใกล้คุณ (ไม่พบผู้หางานในรัศมี ${NEAR_ME_RADIUS_KM} กม.)`
                : `📍 แสดงตำแหน่งใกล้คุณ (ไม่พบบริษัทในรัศมี ${NEAR_ME_RADIUS_KM} กม.)`,
          );
        }

        if (forceCenter || !userInteractedRef.current) {
          try {
            map.setView([latitude, longitude], 12, { animate: true });
          } catch (err) { console.debug('[JobEmployerMap] Map operation skipped:', err?.message); }
        }
      };

      if (customCoords && Array.isArray(customCoords) && customCoords.length === 2) {
        applyCoords(customCoords[0], customCoords[1]);
        return;
      }

      if (!navigator.geolocation) {
        onToast?.('เบราว์เซอร์นี้ไม่รองรับการระบุตำแหน่ง');
        if (
          !hasInitialPositionedRef.current &&
          !userInteractedRef.current &&
          mapInstanceRef.current &&
          mapInstanceRef.current._mapPane
        ) {
          try {
            mapInstanceRef.current.setView(BANGKOK_CENTER, DEFAULT_ZOOM);
          } catch (err) { console.debug('[JobEmployerMap] Map operation skipped:', err?.message); }
        }
        return;
      }

      if (isMountedRef.current) {
        setLocating(true);
        setNearHint('กำลังสแกนหาตำแหน่งใกล้คุณ...');
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!isMountedRef.current) return;
          applyCoords(pos.coords.latitude, pos.coords.longitude);
          setLocating(false);
        },
        () => {
          if (!isMountedRef.current) return;
          setLocating(false);
          setNearHint('ไม่สามารถเข้าถึงพิกัดได้ — แสดงตำแหน่งกรุงเทพฯ');
          if (
            !hasInitialPositionedRef.current &&
            !userInteractedRef.current &&
            mapInstanceRef.current &&
            mapInstanceRef.current._mapPane
          ) {
            try {
              mapInstanceRef.current.setView(BANGKOK_CENTER, DEFAULT_ZOOM);
            } catch (err) { console.debug('[JobEmployerMap] Map operation skipped:', err?.message); }
          }
        },
        { enableHighAccuracy: true, timeout: 8000 },
      );
    },
    [itemsWithCoords, isCandidateMode, onToast],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 1. Initialize Map Once
  useEffect(() => {
    isMountedRef.current = true;
    if (!mapRef.current) return;
    if (mapInstanceRef.current) return;

    if (mapRef.current._leaflet_id) {
      delete mapRef.current._leaflet_id;
    }

    const map = L.map(mapRef.current, {
      center: BANGKOK_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: false,
    });

    map.on('dragstart zoomstart movestart', () => {
      userInteractedRef.current = true;
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    L.control.zoom({ position: 'topright' }).addTo(map);

    const layer = L.layerGroup().addTo(map);
    markersLayerRef.current = layer;
    mapInstanceRef.current = map;

    if (initialUserCoords && Array.isArray(initialUserCoords) && initialUserCoords.length === 2) {
      handleNearMe(initialUserCoords, false);
    }

    setTimeout(() => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.invalidateSize();
        } catch (err) { console.debug('[JobEmployerMap] Map operation skipped:', err?.message); }
      }
    }, 150);

    return () => {
      isMountedRef.current = false;
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch (err) { console.debug('[JobEmployerMap] Map operation skipped:', err?.message); }
        mapInstanceRef.current = null;
      }
      markersLayerRef.current = null;
      userMarkerRef.current = null;
      radiusCircleRef.current = null;
    };
  }, []);

  // 2. Update Markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    const layer = markersLayerRef.current;
    if (!map || !layer || !map._mapPane) return;

    layer.clearLayers();

    const bounds = [];
    itemsWithCoords.forEach((item) => {
      const isNearby = nearbyIds.includes(item.id);
      const marker = L.marker([item.lat, item.lng], {
        icon: buildPinIcon(item, isNearby),
        title: isCandidateMode ? item.name : item.company,
      });

      const popupNode = document.createElement('div');
      popupNode.className = 'job-map-popup';

      const titleStr = isCandidateMode ? item.name || item.title : item.title;
      const companyStr = isCandidateMode ? item.title : item.company;
      const metaStr = isCandidateMode
        ? `${escapeHtml(item.salaryRange || item.salary || '')} · ${escapeHtml(item.province || item.address || '')}`
        : `${escapeHtml(item.salary)} · ${escapeHtml(item.location)}`;

      popupNode.innerHTML = `
        <strong>${escapeHtml(titleStr)}</strong>
        <div class="job-map-popup-company">${escapeHtml(companyStr)}</div>
        <div class="job-map-popup-meta">${metaStr}</div>
      `;
      const linkBtn = document.createElement('button');
      linkBtn.type = 'button';
      linkBtn.className = 'job-map-popup-link';
      linkBtn.textContent = isCandidateMode ? 'ดูโปรไฟล์ผู้หางาน →' : 'ดูรายละเอียดงาน →';
      linkBtn.addEventListener('click', () => {
        onClose?.();
        if (isCandidateMode) {
          if (onSelectCandidate) {
            onSelectCandidate(item);
          }
        } else {
          navigate(`/jobs/detail/${item.id}`);
        }
      });
      popupNode.appendChild(linkBtn);

      marker.bindPopup(popupNode);
      marker.addTo(layer);
      bounds.push([item.lat, item.lng]);
    });

    if (!hasInitialPositionedRef.current && nearbyIds.length === 0 && !userInteractedRef.current) {
      if (bounds.length > 1) {
        try {
          map.fitBounds(bounds, { padding: [48, 48], maxZoom: 12 });
        } catch (err) { console.debug('[JobEmployerMap] Map operation skipped:', err?.message); }
      } else if (bounds.length === 1) {
        try {
          map.setView(bounds[0], 13);
        } catch (err) { console.debug('[JobEmployerMap] Map operation skipped:', err?.message); }
      }
    }

    if (map._mapPane) {
      try {
        map.invalidateSize();
      } catch (err) { console.debug('[JobEmployerMap] Map operation skipped:', err?.message); }
    }
  }, [jobsKey, nearbyIds, isCandidateMode]);

  return (
    <div className='job-employer-map-overlay' onClick={onClose} role='dialog' aria-modal='true'>
      <div
        className='job-employer-map-panel'
        onClick={(e) => e.stopPropagation()}
        role='region'
        aria-label={isCandidateMode ? 'แผนที่ผู้หางาน' : 'แผนที่บริษัทรับสมัคร'}
      >
        <div className='job-employer-map-header'>
          <div>
            <h3>
              {isCandidateMode ? 'แผนที่ผู้หางาน (ใกล้คุณ)' : 'แผนที่บริษัทรับสมัคร (ใกล้คุณ)'}
            </h3>
            <p>
              {itemsWithCoords.length}{' '}
              {isCandidateMode ? 'โปรไฟล์จากผลการค้นหาปัจจุบัน' : 'สถานที่จากผลการค้นหาปัจจุบัน'}
            </p>
          </div>
          <button
            type='button'
            className='job-employer-map-close'
            onClick={onClose}
            aria-label='ปิด'
          >
            &times;
          </button>
        </div>

        <div className='job-employer-map-body'>
          {nearHint && <div className='job-map-hint'>{nearHint}</div>}
          <div className='job-employer-map-canvas' ref={mapRef} />
          <button
            type='button'
            className='job-map-near-me'
            onClick={() => {
              userInteractedRef.current = false;
              handleNearMe(null, true);
            }}
            disabled={locating}
          >
            <span className='near-dot' />
            {locating ? 'กำลังค้นหา...' : 'สแกนหาใกล้ฉัน'}
            <span aria-hidden='true'>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(JobEmployerMap);
